import { AlertCircle } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect } from "react";

import { AppShell } from "components/AppShell";
import { BrandMark } from "components/BrandMark";
import { EmptyState, LoadingRows } from "components/states";
import { EventMeta } from "components/EventMeta";
import { DESCRIPTION_PROSE_CLASS } from "components/description-prose";
import { Card, CardContent } from "components/ui/card";

import { hasHeroFields } from "../event-experience/landing-model";
import { ArchivedBanner } from "./ArchivedBanner";
import { PublicEventBody, PublicEventFooter } from "./PublicEventBody";
import { PublicHero } from "./PublicHero";
import type { PublicEvent } from "./schemas";
import { usePublicEvent } from "./use-public-event";

export function PublicEventPage({ slug }: { slug: string }) {
  const state = usePublicEvent(slug);

  if (state.status === "loading") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-gradient-page text-foreground">
        <div className="w-full max-w-2xl px-5" data-testid="event-loading">
          <LoadingRows rows={4} />
        </div>
      </main>
    );
  }

  if (state.status === "not-found") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-gradient-page text-foreground p-6">
        <EmptyState
          icon={AlertCircle}
          title="Event not found"
          testId="event-not-found"
        >
          This event may have been unpublished or the link may be incorrect.
        </EmptyState>
      </main>
    );
  }

  if (state.status === "error") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-gradient-page text-foreground p-6">
        <EmptyState
          icon={AlertCircle}
          title="Something went wrong"
          testId="event-error"
        >
          {state.error}
        </EmptyState>
      </main>
    );
  }

  return <EventPreview event={state.event} />;
}

/**
 * An event as seen by somebody who is not in it: signed out, or signed in and
 * not yet registered.
 *
 * `session` is what decides the chrome around it. A signed-in reader keeps the
 * application rail and header, because this page is one of their destinations
 * and they must be able to leave it — a secret-gated event used to strand them
 * on a page with no navigation at all, and clearing cookies mid-event dropped
 * the whole shell and left the sign-in card sitting in the middle of the event.
 * A signed-out reader has nowhere to navigate to, so they get the bare page.
 *
 * A signed-out reader sees the hero (or the image block) and the join box and
 * nothing else: the description and the stat tiles are for people who have
 * signed in. A signed-in reader who is not yet registered keeps both, because
 * they can already see what the event is about.
 *
 * An event whose organiser set any of the bold hero's fields opens with
 * `PublicHero` instead of the image block, in both branches. The action is then
 * rendered once, inside that hero.
 */
export function EventPreview({
  event,
  action,
  session,
}: {
  event: PublicEvent;
  action?: ReactNode;
  session?: { email: string | null; onSignOut: () => void };
}) {
  useEffect(() => {
    document.title = event.title;
    return () => {
      document.title = "Holtzbrinck Connect";
    };
  }, [event.title]);

  const bold = hasHeroFields(event);
  // Absent means false: a projection from before archiving has no field.
  const banner = event.archived === true ? <ArchivedBanner /> : null;

  if (session && bold) {
    return (
      <AppShell
        identity={{ email: session.email }}
        onSignOut={session.onSignOut}
        title={event.title}
      >
        <div className="max-w-4xl" data-testid="public-event">
          {banner && <div className="mb-4">{banner}</div>}
          <PublicHero event={event} action={action} variant="shell" />
          <PublicEventBody event={event} />
        </div>
      </AppShell>
    );
  }

  if (session) {
    return (
      <AppShell
        identity={{ email: session.email }}
        onSignOut={session.onSignOut}
        title={event.title}
      >
        {/* The hero image is the event's own identity and both branches show
            it. The title overlay is dropped here only because the shell's
            header already carries the title, and a second copy of it pushes
            the action the reader came for below the fold. */}
        <div className="max-w-3xl" data-testid="public-event">
          {banner && <div className="mb-4">{banner}</div>}
          {event.image && (
            // 4:1 at this column's 768px cap, which is the crop the hero
            // field's own guidance describes.
            <div className="relative mb-6 h-40 w-full overflow-hidden rounded-xl sm:h-48">
              <img
                src={event.image}
                alt={`${event.title} event`}
                className="h-full w-full object-cover"
                style={{
                  objectPosition: `${event.imageFocalX ?? 50}% ${event.imageFocalY ?? 50}%`,
                }}
              />
            </div>
          )}
          <EventMeta
            startDate={event.startDate}
            endDate={event.endDate}
            location={event.location}
            className="mb-6"
          />
          {action}
          {event.description && (
            <Card className="mt-8">
              <CardContent
                className={`pt-6 ${DESCRIPTION_PROSE_CLASS}`}
                data-testid="event-description"
                dangerouslySetInnerHTML={{ __html: event.description }}
              />
            </Card>
          )}
        </div>
      </AppShell>
    );
  }

  if (bold) {
    return (
      <main
        className="min-h-svh bg-background text-foreground"
        data-testid="public-event"
      >
        {event.archived === true && <ArchivedBanner variant="strip" />}
        <PublicHero event={event} action={action} variant="page" />
        <PublicEventFooter event={event} />
      </main>
    );
  }

  return (
    <main
      className="min-h-screen bg-gradient-page text-foreground"
      data-testid="public-event"
    >
      {/* A signed-out visitor has no rail, because they have nowhere to
          navigate. They still need to know which product they are looking at:
          without this the page reads as an unrelated site with a sign-in form
          dropped into the middle of it. */}
      <div className="glass-header flex items-center gap-3 px-5 py-3 md:px-10">
        <BrandMark />
        <p className="font-display text-sm font-semibold text-foreground">
          connect
        </p>
      </div>

      {event.archived === true && <ArchivedBanner variant="strip" />}

      {event.image && (
        <div className="relative h-64 w-full overflow-hidden sm:h-80 md:h-96">
          <img
            src={event.image}
            alt={`${event.title} event`}
            className="h-full w-full object-cover"
            style={{
              objectPosition: `${event.imageFocalX ?? 50}% ${event.imageFocalY ?? 50}%`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-0 w-full px-5 pb-8 md:px-10">
            <h1 className="font-display text-3xl font-semibold text-white drop-shadow-lg wrap-anywhere md:text-4xl">
              {event.title}
            </h1>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-3xl px-5 py-8 md:px-10">
        {!event.image && (
          <div className="relative overflow-hidden rounded-xl bg-dusk-hero px-6 py-12 shadow-card md:px-10">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-hb-purple-fill/40 blur-3xl" />
            <h1 className="font-display relative text-3xl font-semibold text-white wrap-anywhere md:text-4xl">
              {event.title}
            </h1>
          </div>
        )}

        <EventMeta
          startDate={event.startDate}
          endDate={event.endDate}
          location={event.location}
          className="mt-4"
        />

        {action && <div className="mt-6">{action}</div>}
      </div>
    </main>
  );
}
